// The app-facing analytics API. Everything in app/app calls track() and
// identify() from here and knows nothing else about umami.
//
// Analytics is OPTIONAL BY CONSTRUCTION. When ANALYTICS_MODE=off (or the
// script simply fails to load, or an extension blocks it), window.umami never
// appears and every function here is a no-op that throws nothing. No call site
// gets a conditional, and no feature can break because measurement is
// switched off.
//
// See services/analytics.js for the server half - the loader, the proxied
// tracker, and the collect endpoint, all served from this origin under /w.

import beforeSend, { armPageExit, setVisitorRole, resolveVisitorAsAnonymous } from './beforeSend.js';
import { normalizePath } from './urls.js';

// Must match BEFORE_SEND_FN in services/analytics.js, which writes it onto the
// tracker's script tag as data-before-send. Two processes, one string - kept
// short and named in both places rather than plumbed through the loader,
// because the loader is generated JavaScript and a value that has to survive
// being interpolated into it is a value that can be interpolated wrong.
const BEFORE_SEND_FN = '__wuSend';

// The tracker script is <script async> (see root.jsx), so the first events on
// a landing page routinely happen before it exists. They are queued rather
// than dropped: "which sign-up button did they press" is answerable only if
// the answer survives a race with a script tag.
//
// Bounded on both axes. If the script never arrives - blocked, offline,
// ANALYTICS_MODE=off - the queue must not grow for the life of the tab.
const QUEUE_LIMIT = 50;
const QUEUE_TIMEOUT_MS = 10_000;

let queue = [];
let queueExpired = false;
let installed = false;

function umami() {
  return typeof window !== 'undefined' ? window.umami : undefined;
}

function flush() {
  const api = umami();
  if (!api) return;
  const pending = queue;
  queue = [];
  pending.forEach((run) => {
    try {
      run(api);
    } catch {
      // A tracking call must never surface as an app error.
    }
  });
}

function enqueue(run) {
  const api = umami();
  if (api) {
    try {
      run(api);
    } catch {
      /* never let measurement break the app */
    }
    return;
  }
  if (queueExpired) return;
  if (queue.length >= QUEUE_LIMIT) queue.shift();
  queue.push(run);
}

// Called once from root.jsx. Publishes the beforeSend hook the tracker looks
// up by name, then waits for window.umami to appear so the queue can drain.
export function installAnalytics() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Defined before the tracker loads, but that ordering isn't actually
  // required - the tracker resolves this by name at send time, not at load.
  window[BEFORE_SEND_FN] = beforeSend;

  if (umami()) {
    flush();
    return;
  }

  // Polled rather than hooked to the script tag's onload: the tag is injected
  // by /w/b.js, which this module deliberately knows nothing about beyond its
  // URL in root.jsx.
  const started = Date.now();
  const timer = setInterval(() => {
    if (umami()) {
      clearInterval(timer);
      flush();
      return;
    }
    if (Date.now() - started > QUEUE_TIMEOUT_MS) {
      clearInterval(timer);
      queueExpired = true;
      queue = [];
    }
  }, 100);
}

// A named event. `data` is umami's own event-data shape: flat, <=50 keys,
// strings capped at 500 chars. Keep values LOW-CARDINALITY - they become
// dashboard rows - and never put a name, email, phone, VIN or customer detail
// in one. Nothing here inspects the values, so that rule lives at the call
// sites and in this comment.
export function track(name, data) {
  enqueue((api) => api.track(name, data));
}

// A pageview, with the URL normalized (see urls.js). Passed explicitly because
// the tracker's own auto-pageview is turned off - it would send the raw
// location, ids and query string included.
export function trackPageview(pathname) {
  armPageExit();
  enqueue((api) =>
    api.track((props) => ({
      ...props,
      url: normalizePath(pathname),
      // props.referrer is whatever the tracker recorded for the PREVIOUS page;
      // beforeSend normalizes it, so nothing extra is needed here.
    }))
  );
}

// Identity stitching. Called with the opaque auth-service user id and nothing
// else - never the email, name or phone. Umami associates the whole current
// session with the identifier, which is what connects the anonymous landing
// page and tour activity that came BEFORE the account to the account itself.
//
// Never called for an admin or an anonymous tour-guide session - see
// routes/protected.jsx, which is the only caller and the only place that knows.
export function identify(userId) {
  if (!userId) return;
  enqueue((api) => api.identify(userId));
}

export { setVisitorRole, resolveVisitorAsAnonymous };
